import { Box, Drawer, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowLeft2,
  Category,
  Element3,
  More,
  People,
  ReceiptItem,
  Setting2,
  ShoppingCart,
  type Icon as IconsaxIcon,
} from "iconsax-react";
import { Link } from "@tanstack/react-router";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MOBILE_MORE_ITEMS, MOBILE_NAV_ITEMS } from "@/lib/navigation/navItems";
import type { MobileBottomNavProps } from "./MobileBottomNav.types";
import * as styles from "./MobileBottomNav.styles";

export type { MobileBottomNavProps } from "./MobileBottomNav.types";

const NAV_ICONS: Record<string, IconsaxIcon> = {
  "/": Element3,
  "/orders": ShoppingCart,
  "/products": Category,
  "/customers": People,
  "/billing": ReceiptItem,
  "/settings": Setting2,
};

function isNavActive(path: string, activePath: string): boolean {
  if (path === "/") return activePath === "/";
  return activePath.startsWith(path);
}

export const MobileBottomNav = memo(function MobileBottomNav({ activePath }: MobileBottomNavProps) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const items = useMemo(
    () =>
      MOBILE_NAV_ITEMS.map((item) => ({
        ...item,
        Icon: NAV_ICONS[item.to] ?? More,
        active: isNavActive(item.to, activePath),
      })),
    [activePath],
  );

  const moreItems = useMemo(
    () =>
      MOBILE_MORE_ITEMS.map((item) => ({
        ...item,
        Icon: NAV_ICONS[item.to] ?? More,
        active: isNavActive(item.to, activePath),
      })),
    [activePath],
  );

  const isMoreActive = isMoreOpen || moreItems.some((item) => item.active);
  const iconColor = (active: boolean) =>
    active ? theme.palette.primary.main : theme.palette.text.secondary;

  return (
    <>
      <Box component="nav" sx={styles.root} aria-label={t("navbar.menuLabel")}>
        {items.map(({ to, labelKey, Icon, active }) => (
          <Box key={to} component={Link} to={to} sx={styles.navItem(active)}>
            <Icon size={22} variant={active ? "Bold" : "Linear"} color={iconColor(active)} />
            {t(labelKey)}
          </Box>
        ))}
        <Box
          component="button"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isMoreOpen}
          onClick={() => setIsMoreOpen(true)}
          sx={styles.navItem(isMoreActive)}
        >
          <More size={22} variant={isMoreActive ? "Bold" : "Linear"} color={iconColor(isMoreActive)} />
          {t("nav.more")}
        </Box>
      </Box>

      <Drawer
        anchor="bottom"
        open={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
        slotProps={{ paper: { sx: styles.sheetPaper } }}
      >
        <Box sx={styles.sheetHandle} />
        <Typography sx={styles.sheetTitle}>{t("nav.more")}</Typography>
        <Box component="ul" sx={styles.sheetList}>
          {moreItems.map(({ to, labelKey, Icon, active }) => (
            <Box component="li" key={to}>
              <Box
                component={Link}
                to={to}
                onClick={() => setIsMoreOpen(false)}
                sx={styles.sheetItem(active)}
              >
                <Box sx={styles.sheetIconWrap(active)}>
                  <Icon size={20} variant={active ? "Bold" : "Linear"} color={iconColor(active)} />
                </Box>
                <Box component="span" sx={styles.sheetLabel}>
                  {t(labelKey)}
                </Box>
                <ArrowLeft2 size={16} variant="Linear" color={theme.palette.text.secondary} />
              </Box>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
});
